import {
    Drawer,
    Avatar,
    Button,
    Card,
    Row,
    Col,
    Tag,
    Form,
    Input,
    Select,
    Space,
    message,
    Modal,
    Tabs,
    Typography,
    Badge,
    Spin,
    Empty,
    Layout,
    Alert,
} from "antd";
import { useState, useEffect } from "react";
import {
    SaveOutlined,
    KeyOutlined,
    UserOutlined,
    TeamOutlined,
    CrownOutlined,
    MailOutlined,
    PhoneOutlined,
    SettingOutlined,
    LockOutlined,
    AntDesignOutlined,
} from "@ant-design/icons";
import { useUpdateItemMutation, useItemsFine } from "../_actions/app/items";
import { getPublicImageURL } from "../_helpers/image";
import { useMemo } from "react";
import { getTime } from "../_helpers/app/dt";
import AvatarPhoto from "../media/imgs/avatar2.jpeg";

const { Option } = Select;
const { Text, Title } = Typography;
const { Content } = Layout;

function UserDetailsModal({ open, setOpen, user, onUserUpdate }) {
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("personal");
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [orgModalOpen, setOrgModalOpen] = useState(false);
    const updateUserMutation = useUpdateItemMutation();

    const userOrgFilter = useMemo(() => {
        if (!user) return "";
        return `fields=id,owner,name,logo,users.directus_users_id&filter={"_or":[{"owner":{"_eq":"${user.id}"}},{"users":{"directus_users_id":{"_eq":"${user.id}"}}}]}`;
    }, [user]);

    const {
        data: userOrganizations,
        isLoading: orgLoading,
        refetch: refetchOrganizations,
    } = useItemsFine("qdb_organization", userOrgFilter, true, 0);

    const ownedOrgFilter = useMemo(() => {
        if (!user || !user.owned_organizations) return "";
        return `fields=id,owner,name,logo,users.directus_users_id&filter={"id":{"_in":[${user.owned_organizations
            .map((id) => `"${id}"`)
            .join(",")}]}}`;
    }, [user]);

    const { data: ownedOrganizationsData, isLoading: ownedOrgLoading } =
        useItemsFine("qdb_organization", ownedOrgFilter, true, 0);

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                status: user.status,
                user_role: user.user_role,
                mobile: user.mobile,
                irole: user.irole,
            });
        }
    }, [user, form]);

    if (!user) return null;

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            updateUserMutation.mutate(
                {
                    collection: "directus_users",
                    id: user.id,
                    data: values,
                },
                {
                    onSuccess: () => {
                        message.success("User updated successfully");
                        onUserUpdate?.();
                    },
                    onError: (error) => {
                        message.error("Failed to update user");
                        console.error("Update error:", error);
                    },
                }
            );
        } catch (error) {
            message.error("Please check the form for errors");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        try {
            setResetPasswordLoading(true);
            const values = await passwordForm.validateFields();

            updateUserMutation.mutate(
                {
                    collection: "directus_users",
                    id: user.id,
                    data: { password: values.new_password },
                },
                {
                    onSuccess: () => {
                        message.success("Password updated successfully");
                        setPasswordModalOpen(false);
                        passwordForm.resetFields();
                    },
                    onError: (error) => {
                        message.error("Failed to update password");
                        console.error("Password update error:", error);
                    },
                }
            );
        } catch (error) {
            message.error("Please check the form for errors");
        } finally {
            setResetPasswordLoading(false);
        }
    };

    const handleOrganizationClick = (organization) => {
        setSelectedOrganization(organization);
        setOrgModalOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "active":
                return "success";
            case "inactive":
                return "warning";
            case "suspended":
                return "error";
            default:
                return "default";
        }
    };

    const OrganizationCards = () => {
        if (orgLoading) {
            return (
                <Space
                    direction="vertical"
                    size={20}
                    align="center"
                    style={{ width: "100%", padding: "50px 0" }}
                >
                    <Spin size="large" />
                    <Title level={4} type="secondary" style={{ margin: 0 }}>
                        Loading organizations...
                    </Title>
                </Space>
            );
        }

        if (!userOrganizations || userOrganizations.length === 0) {
            return (
                <Empty
                    image={<TeamOutlined style={{ fontSize: "40px" }} />}
                    description={
                        <Space direction="vertical" size="small">
                            <Title level={4} style={{ margin: 0 }}>
                                No organizations found
                            </Title>
                            <Text type="secondary">
                                This user is not associated with any
                                organizations.
                            </Text>
                        </Space>
                    }
                    style={{ padding: "20px 0" }}
                />
            );
        }

        return (
            <Row
                gutter={[
                    { xs: 8, sm: 12, md: 16, lg: 20 },
                    { xs: 8, sm: 12, md: 16, lg: 20 },
                ]}
            >
                {userOrganizations
                    .filter((item) => item.owner !== user.id)
                    .map((item) => {
                        return (
                            <Col
                                xs={24}
                                sm={12}
                                md={8}
                                lg={6}
                                xl={4}
                                key={item.id}
                            >
                                <Card
                                    hoverable
                                    size="small"
                                    onClick={() =>
                                        handleOrganizationClick(item)
                                    }
                                    bordered
                                    styles={{
                                        body: {
                                            padding: "12px",
                                        },
                                    }}
                                >
                                    <img
                                        src={getPublicImageURL(item.logo)}
                                        alt={item.name}
                                        style={{
                                            width: "100%",
                                            height: "100px",
                                            objectFit: "contain",
                                            marginBottom: "12px",
                                        }}
                                    />

                                    <Text ellipsis={{ tooltip: item.name }}>
                                        {item.name}
                                    </Text>
                                </Card>
                            </Col>
                        );
                    })}
            </Row>
        );
    };

    const OwnedOrganizationCards = () => {
        if (ownedOrgLoading) {
            return (
                <Space
                    direction="vertical"
                    size={20}
                    align="center"
                    style={{ width: "100%", padding: "50px 0" }}
                >
                    <Spin size="large" />
                    <Title level={4} type="secondary" style={{ margin: 0 }}>
                        Loading owned organizations...
                    </Title>
                </Space>
            );
        }

        if (
            !user.owned_organizations ||
            user.owned_organizations.length === 0
        ) {
            return (
                <Empty
                    image={<CrownOutlined style={{ fontSize: "40px" }} />}
                    description={
                        <Space direction="vertical" size="small">
                            <Title level={4} style={{ margin: 0 }}>
                                No owned organizations found
                            </Title>
                            <Text type="secondary">
                                This user doesn't own any organizations.
                            </Text>
                        </Space>
                    }
                    style={{ padding: "20px 0" }}
                />
            );
        }

        const organizationsToShow =
            ownedOrganizationsData || user.owned_organizations;

        return (
            <Row
                gutter={[
                    { xs: 8, sm: 12, md: 16, lg: 20 },
                    { xs: 8, sm: 12, md: 16, lg: 20 },
                ]}
            >
                {organizationsToShow.map((item, index) => {
                    console.log("Organization item:", item);
                    const isFullData = typeof item === "object" && item.id;
                    const orgId = isFullData ? item.id : item;
                    const orgName = isFullData
                        ? item.name
                        : `Organization ${index + 1}`;
                    const orgLogo = isFullData ? item.logo : null;

                    return (
                        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={orgId}>
                            <Card
                                hoverable={isFullData}
                                size="small"
                                onClick={() =>
                                    isFullData && handleOrganizationClick(item)
                                }
                                styles={{
                                    body: {
                                        padding: "12px",
                                    },
                                }}
                            >
                                <img
                                    src={
                                        orgLogo
                                            ? getPublicImageURL(item.logo)
                                            : AvatarPhoto
                                    }
                                    alt={item.name}
                                    style={{
                                        width: "100%",
                                        height: "100px",
                                        objectFit: "contain",
                                        paddingBottom: "10px",
                                    }}
                                />

                                <Text ellipsis={{ tooltip: orgName }}>
                                    {orgName}
                                </Text>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    };

    const tabItems = [
        {
            key: "personal",
            label: "Profile Information",
            icon: <UserOutlined />,
            children: (
                <Card bordered>
                    <Form form={form} layout="vertical">
                        <Row
                            gutter={[
                                { xs: 8, sm: 16, md: 20, lg: 28 },
                                { xs: 8, sm: 16, md: 20, lg: 28 },
                            ]}
                        >
                            {/* Basic Information */}
                            <Col span={24}>
                                <Card
                                    title={
                                        <Space>
                                            <UserOutlined />
                                            <Title
                                                level={5}
                                                style={{ margin: 0 }}
                                            >
                                                Basic Information
                                            </Title>
                                        </Space>
                                    }
                                    size="small"
                                    styles={{
                                        header: {
                                            backgroundColor: "#f8f9fa",
                                        },
                                    }}
                                >
                                    <Row
                                        gutter={[
                                            { xs: 8, sm: 12, md: 16, lg: 20 },
                                            { xs: 8, sm: 12, md: 16, lg: 20 },
                                        ]}
                                    >
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label={<Text>First Name</Text>}
                                                name="first_name"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            "First name is required",
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    size="default"
                                                    placeholder="Enter first name"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label={<Text>Last Name</Text>}
                                                name="last_name"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            "Last name is required",
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    size="default"
                                                    placeholder="Enter last name"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label={<Text>Email</Text>}
                                                name="email"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            "Email is required",
                                                    },
                                                    {
                                                        type: "email",
                                                        message:
                                                            "Please enter a valid email",
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    size="default"
                                                    prefix={<MailOutlined />}
                                                    placeholder="Enter email"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label={<Text>Mobile</Text>}
                                                name="mobile"
                                            >
                                                <Input
                                                    size="default"
                                                    prefix={<PhoneOutlined />}
                                                    placeholder="Enter mobile number"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            {/* Account Settings */}
                            <Col xs={24} lg={12}>
                                <Card
                                    title={
                                        <Space>
                                            <SettingOutlined />
                                            <Title
                                                level={5}
                                                style={{ margin: 0 }}
                                            >
                                                Account Settings
                                            </Title>
                                        </Space>
                                    }
                                    size="small"
                                    styles={{
                                        header: {
                                            backgroundColor: "#f8f9fa",
                                        },
                                    }}
                                >
                                    <Form.Item
                                        label={<Text>Status</Text>}
                                        name="status"
                                    >
                                        <Select
                                            size="default"
                                            placeholder="Select status"
                                        >
                                            <Option value="active">
                                                <Badge
                                                    status="success"
                                                    text={<Text>Active</Text>}
                                                />
                                            </Option>
                                            <Option value="inactive">
                                                <Badge
                                                    status="warning"
                                                    text={<Text>Inactive</Text>}
                                                />
                                            </Option>
                                            <Option value="suspended">
                                                <Badge
                                                    status="error"
                                                    text={
                                                        <Text>Suspended</Text>
                                                    }
                                                />
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item
                                        label={<Text>Role</Text>}
                                        name="user_role"
                                    >
                                        <Select
                                            size="default"
                                            placeholder="Select role"
                                        >
                                            <Option value="public">
                                                Public
                                            </Option>
                                            <Option value="admin">Admin</Option>
                                            <Option value="moderator">
                                                Moderator
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item
                                        label={<Text>Internal role</Text>}
                                        name="irole"
                                    >
                                        <Select
                                            size="default"
                                            placeholder="Select internal role"
                                        >
                                            <Option value="none">None</Option>
                                            <Option value="reviewer">
                                                Reviewer
                                            </Option>
                                            <Option value="editor">
                                                Editor
                                            </Option>
                                            <Option value="admin">Admin</Option>
                                        </Select>
                                    </Form.Item>
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                {/* Password Management */}
                                <Card
                                    title={
                                        <Space>
                                            <LockOutlined />
                                            <Title
                                                level={5}
                                                style={{ margin: 0 }}
                                            >
                                                Password Management
                                            </Title>
                                        </Space>
                                    }
                                    size="small"
                                    styles={{
                                        header: {
                                            backgroundColor: "#f8f9fa",
                                        },
                                    }}
                                >
                                    <Text
                                        type="secondary"
                                        style={{
                                            display: "block",
                                            marginBottom: 20,
                                        }}
                                    >
                                        Set a new password for this user. The
                                        user will be able to login immediately
                                        with the new password.
                                    </Text>
                                    <Button
                                        icon={<KeyOutlined />}
                                        onClick={() =>
                                            setPasswordModalOpen(true)
                                        }
                                        size="default"
                                        block
                                    >
                                        Set New Password
                                    </Button>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            ),
        },
        {
            key: "organizations",
            label: "Organizations",
            icon: <TeamOutlined />,
            children: (
                <div style={{ padding: "12px 0" }}>
                    <Card
                        title={
                            <Space>
                                <CrownOutlined />
                                <Text>Owned Organizations</Text>
                            </Space>
                        }
                        size="small"
                        styles={{
                            header: {
                                backgroundColor: "#f8f9fa",
                            },
                        }}
                        style={{ marginBottom: 18 }}
                    >
                        <OwnedOrganizationCards />
                    </Card>

                    <Card
                        title={
                            <Space>
                                <TeamOutlined />
                                <Text>Member Organizations</Text>
                            </Space>
                        }
                        size="small"
                        styles={{
                            header: {
                                backgroundColor: "#f8f9fa",
                            },
                        }}
                    >
                        <OrganizationCards />
                    </Card>
                </div>
            ),
        },
        {
            key: "additional_data",
            label: "Creations",
            icon: <AntDesignOutlined />,
            children: (
                <div style={{ padding: "12px 0" }}>
                    <Empty
                        description={
                            <Text type="secondary">
                                No creations to display
                            </Text>
                        }
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <Drawer
                width={window.innerWidth < 768 ? "90%" : "65%"}
                open={open}
                onClose={() => setOpen(false)}
                closable={false}
                title={
                    <div style={{ padding: "8px 0" }}>
                        <Typography.Title
                            level={window.innerWidth < 768 ? 4 : 3}
                            style={{ marginBottom: 16, marginTop: 0 }}
                        >
                            {user.first_name} {user.last_name}
                        </Typography.Title>

                        <div style={{ marginBottom: 16 }}>
                            <Space align="center">
                                {user.mobile ? (
                                    <PhoneOutlined />
                                ) : (
                                    <MailOutlined />
                                )}
                                <Text type="secondary">
                                    {user.mobile ? user.mobile : user.email}
                                </Text>
                            </Space>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Space size={[8, 8]} wrap>
                                <Tag color={getStatusColor(user.status)}>
                                    {user.status?.toUpperCase()}
                                </Tag>
                                <Tag color="blue">{user.role?.name}</Tag>
                                {user.is_dev && (
                                    <Tag color="orange">Developer</Tag>
                                )}
                            </Space>
                        </div>

                        {user.last_access && (
                            <Text type="secondary">
                                Last online: {getTime(user.last_access, "r")}
                            </Text>
                        )}
                    </div>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        size="Default"
                        loading={loading}
                        onClick={handleSave}
                        style={{
                            position: "absolute",
                            top: 20,
                            right: 32,
                        }}
                    >
                        Save Changes
                    </Button>
                }
            >
                <Tabs
                    destroyInactiveTabPane
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    size="default"
                    type="card"
                    // tabBarGutter={24}
                />
            </Drawer>

            {/* Password Reset Modal */}
            <Modal
                title={
                    <Space align="baseline" size="small">
                        <LockOutlined style={{ marginRight: 4 }} />
                        <Space direction="vertical" size={0}>
                            <Text>Set New Password</Text>
                            <Text type="secondary">
                                for {user.first_name} {user.last_name}
                            </Text>
                        </Space>
                    </Space>
                }
                open={passwordModalOpen}
                onOk={handleResetPassword}
                onCancel={() => {
                    setPasswordModalOpen(false);
                    passwordForm.resetFields();
                }}
                confirmLoading={resetPasswordLoading}
                okText="Update Password"
                cancelText="Cancel"
                width={500}
            >
                <Form form={passwordForm} layout="vertical" size="small">
                    <Form.Item
                        label="New Password"
                        name="new_password"
                        rules={[
                            { required: true, message: "Required" },
                            { min: 8, message: "Minimum 8 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Enter new password" />
                    </Form.Item>

                    <Form.Item
                        label="Confirm Password"
                        name="confirm_password"
                        dependencies={["new_password"]}
                        rules={[
                            { required: true, message: "Required" },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (
                                        !value ||
                                        getFieldValue("new_password") === value
                                    ) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(
                                        new Error("Passwords don't match")
                                    );
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirm new password" />
                    </Form.Item>

                    <Alert
                        message={
                            <Space direction="vertical" size={2}>
                                <Text>• Minimum 8 characters</Text>
                                <Text>• Immediate login with new password</Text>
                            </Space>
                        }
                        type="info"
                    />
                </Form>
            </Modal>

            {/* {selectedOrganization && (
                <OrganizationModal
                    key={selectedOrganization.id}
                    open={orgModalOpen}
                    setOpen={setOrgModalOpen}
                    organization={selectedOrganization}
                    onOrganizationUpdate={handleOrganizationUpdate}
                />
            )} */}
        </>
    );
}

export default UserDetailsModal;
